"use client";

// French navbar for widget + platform pages and their how-to guides
// (FR-1). Copy of widgetNavbarFr.comp.tsx with the English
// navbar's platform paths: tabs point at /widgets/<widget>/<platform>[/...].
// Each tab links to the French twin when one exists, otherwise to the English
// page with an EN tag. Templates pages are English-only.
import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useInView } from "react-intersection-observer";
import {
  StyledFloatingButton,
  StyledWidgetNav,
} from "../widgetNavbar/widgetNavbar.styles";
import { IPlatform, IPlugin } from "../../types";
import { CustomLink } from "../link/link.comp";
import { trackEvent } from "@/lib/track";
import { createMobileAwareClickHandler } from "../../helpers/mobile-redirect";
import { frHref, hasFrTwin } from "@/i18n/fr/routes";
import { EnTag } from "./langSwitch.comp";

interface IWidgetSpokeNavbarFrProps {
  widget: IPlugin;
  platform: IPlatform;
  active: "overview" | "how-to-add";
  showTemplatesTab?: boolean;
}

export const WidgetSpokeNavbarFr = ({
  widget,
  platform,
  active,
  showTemplatesTab,
}: IWidgetSpokeNavbarFrProps) => {
  const [loaded, setLoaded] = useState(false);
  const { ref, inView } = useInView({ threshold: 0 });
  const [shake, setShake] = useState(false);
  const [playgroundPassed, setPlaygroundPassed] = useState(true);

  const widgetSlug = widget.seoSlug || widget.slug;
  const enOverview = `/widgets/${widgetSlug}/${platform.slug}`;
  const enHowTo = `${enOverview}/how-to-add`;
  const overviewPath = frHref(enOverview);
  const howToPath = frHref(enHowTo);
  const editorPath = `/${widget.slug}/editor`;

  useEffect(() => {
    setLoaded(true);
    const interval = setInterval(() => {
      setShake(true);
      setTimeout(() => setShake(false), 1000);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Same rule as the English navbar: the floating CTA waits until the visitor
  // has scrolled past the live editor playground, when the page has one.
  useEffect(() => {
    const playground = document.querySelector("[data-cn-live-playground]");
    if (!playground) return;
    setPlaygroundPassed(false);
    const observer = new IntersectionObserver(
      ([entry]) =>
        setPlaygroundPassed(
          !entry.isIntersecting && entry.boundingClientRect.bottom < 0
        ),
      { threshold: 0 }
    );
    observer.observe(playground);
    return () => observer.disconnect();
  }, []);

  function renderNavContent(isFixed?: boolean) {
    return (
      <div className="links">
        {isFixed !== undefined && (
          <Link href={overviewPath} className="logo">
            <Image src={widget.logo} alt={`Logo : ${widget.name}`} width={30} height={30} />
            {widget.name}
          </Link>
        )}
        <ul>
          <li>
            <Link className={active === "overview" ? "active" : ""} href={overviewPath}>
              Présentation
              {!hasFrTwin(enOverview) && <EnTag />}
            </Link>
          </li>
          {showTemplatesTab !== false && (
            <li className="templates">
              <Link href={`${enOverview}/templates`} hrefLang="en">
                Modèles
                <EnTag />
              </Link>
            </li>
          )}
          <li className="how-to-add">
            <Link
              className={active === "how-to-add" ? "active" : ""}
              href={howToPath}
              hrefLang={hasFrTwin(enHowTo) ? "fr" : "en"}
            >
              Intégration
              {!hasFrTwin(enHowTo) && <EnTag />}
            </Link>
          </li>
          {!inView && (
            <>
              <li>
                <Link href={frHref("/pricing")}>
                  Tarifs
                  {!hasFrTwin("/pricing") && <EnTag />}
                </Link>
              </li>
              <li className="cta-li">
                <CustomLink
                  external
                  title={`${widget.name} pour ${platform.title}, essai gratuit`}
                  href={editorPath}
                  onClick={createMobileAwareClickHandler(() => {}, editorPath)}
                  id="menu-try-cta"
                >
                  Essai gratuit
                </CustomLink>
              </li>
            </>
          )}
        </ul>
      </div>
    );
  }

  return (
    <>
      <StyledWidgetNav ref={ref} className={`${inView || !loaded ? "show" : ""}`}>
        {renderNavContent(false)}
      </StyledWidgetNav>
      <StyledWidgetNav className={`fixed ${!inView && loaded ? "show" : ""}`}>
        {renderNavContent(true)}
      </StyledWidgetNav>
      <StyledFloatingButton
        className={`floating-button ${shake ? "shake" : ""}`}
        style={{ display: !inView && loaded && playgroundPassed ? "block" : "none" }}
        href={editorPath}
        onClick={createMobileAwareClickHandler(() => {
          trackEvent(`Floating try for free`, {
            serviceName: widget.serviceName,
            locale: "fr",
          });
        }, editorPath)}
        id="floating-try-cta"
      >
        Essayez gratuitement !
      </StyledFloatingButton>
    </>
  );
};
